// Member notes: the fixed notes rule and the shift-end review (architecture sprint-15 §3.2, §5.3, §7.4, A-6).
// Pure and dependency-free (shared-purity.test.ts), shared by the server (authoritative) and the client (which
// menus and buttons render).
//
// A note's kind is stored when it is written: 'self' (the author wrote it about themselves) or 'staff'.
// Owner decision 2 (2026-10-29): a Supervisor's self-note needs no review. It is stored with
// reviewed_at = created_at and reviewed_by_id = the author. Approving a note about yourself is refused
// (403 NOTE_SELF_APPROVE), so "reviewed by its own subject" can only mean "written as no-review".

export const STAFF_EDIT_WINDOW_MS = 86_400_000;

export type NoteKind = 'self' | 'staff';
export type NoteReviewStatus = 'pending' | 'approved';

/** The stored facts the rules read (a self-note's subject is always its author: DB CHECK). */
export interface NoteRuleFacts {
  kind: NoteKind;
  authorId: string;
  subjectUserId: string;
  /** Epoch ms. */
  createdAtMs: number;
  reviewedAtMs: number | null;
  reviewedById: string | null;
}

/** A Supervisor's self-note (owner decision 2): stored as reviewed by its own subject at write time. */
export function isNoReviewSelfNote(n: Pick<NoteRuleFacts, 'kind' | 'subjectUserId' | 'reviewedById'>): boolean {
  return n.kind === 'self' && n.reviewedById !== null && n.reviewedById === n.subjectUserId;
}

/** null for staff notes and no-review self-notes; else pending until approved. */
export function reviewStatus(n: Pick<NoteRuleFacts, 'kind' | 'subjectUserId' | 'reviewedById' | 'reviewedAtMs'>): NoteReviewStatus | null {
  if (n.kind === 'staff' || isNoReviewSelfNote(n)) return null;
  return n.reviewedAtMs === null ? 'pending' : 'approved';
}

export type AuthorEditDecision = 'ok' | 'NOTE_ALREADY_APPROVED' | 'NOTE_EDIT_WINDOW_CLOSED' | 'NOTE_NOT_EDITABLE';

/**
 * §5.3: who may edit or delete a live note. Admins always; the author of a pending self-note until it is
 * approved; the author of a staff note (or of a no-review self-note) while now < created_at + 24 h.
 */
export function authorEditDecision(n: NoteRuleFacts, actorId: string, isAdmin: boolean, nowMs: number): AuthorEditDecision {
  if (isAdmin) return 'ok';
  if (n.authorId !== actorId) return 'NOTE_NOT_EDITABLE';
  const status = reviewStatus(n);
  if (status === 'pending') return 'ok';
  if (status === 'approved') return 'NOTE_ALREADY_APPROVED';
  return nowMs < n.createdAtMs + STAFF_EDIT_WINDOW_MS ? 'ok' : 'NOTE_EDIT_WINDOW_CLOSED';
}

export function authorCanEdit(n: NoteRuleFacts, actorId: string, isAdmin: boolean, nowMs: number): boolean {
  return authorEditDecision(n, actorId, isAdmin, nowMs) === 'ok';
}

/** A-6: one review item per shift; a note added later (a new max created_at) is a new event. */
export function reviewEventId(shiftId: string, latestNoteAtMs: number): string {
  return `notes:${shiftId}:${latestNoteAtMs}`;
}

// ── DTOs (shared with the client) ───────────────────────────────────────────────────────────────────────

export interface NoteReviewDto {
  status: NoteReviewStatus;
  reviewedAt: string | null;
  reviewedBy: { id: string; name: string } | null;
}

/** Same shape as shifts.ts NoteShiftDto (src/shared modules may not depend on each other). */
export interface NoteShiftRefDto {
  id: string;
  startedAt: string;
  endedAt: string | null;
  voided: boolean;
}

export interface OwnNoteDto {
  id: string;
  project: { id: string; name: string; color: string };
  date: string;
  description: string;
  shift: NoteShiftRefDto | null;
  /** No approver and no time for the author (solution §2); null = no review needed (a Supervisor's self-note). */
  review: { status: NoteReviewStatus } | null;
  createdAt: string;
  updatedAt: string;
  edited: boolean;
  canEdit: boolean;
}

export interface OwnNoteProjectDto {
  id: string;
  name: string;
  color: string;
  shiftEnabled: boolean;
  openShiftId: string | null;
  /** Owner decision 2: true for a Member row (their self-notes are reviewed), false for a Supervisor row. */
  reviewed: boolean;
}

export interface NotesReviewItemDto {
  eventId: string;
  projectId: string;
  projectName: string;
  member: { id: string; name: string };
  shift: { id: string; startedAt: string; endedAt: string; endReason: 'ended' | 'auto_closed' | 'corrected' | null };
  pendingCount: number;
  latestNoteAt: string;
}

export interface NotesReviewDto {
  /** Reviewable notes in scope. */
  total: number;
  projects: Array<{ projectId: string; name: string; color: string; count: number }>;
  /** latestNoteAt desc, at most 100. */
  items: NotesReviewItemDto[];
  notify: boolean;
}

export const NOTES_REVIEW_ITEM_LIMIT = 100;
export const APPROVE_MAX_IDS = 100;
